import { useCallback, useMemo, useState } from 'react';
import type { RouteProp } from '@react-navigation/native';
import { useRoute } from '@react-navigation/native';
import { useNoticeBannersStackContext } from '@sweetgreen/garnish';

import { useCustomer } from '@order/Customer';
import { useCart } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';
import { getUrqlError } from '@order/utils';

import type { ModalStackParamList } from '../../navigation/AppNavigation.props';
import { useAddressesTelemetry } from './AddressForm.telemetry';
import type { AddressForm, AddressForm as Form } from './AddressForm.types';
import {
  useAddAddressMutation,
  useDeleteAddressMutation,
  useUpdateAddressMutation,
} from './GraphQL/Address.generated';

export const useAddressHandlers = (props?: AddressHandlersProps) => {
  const addressToEdit = props?.addressToEdit;
  const checkExistingBag = props?.checkExistingBag;
  const onClose = props?.onClose;

  // ─── STATE ────────────────────────────────────────────────────────
  const { customer, isFetchingCustomer, setCustomerData } = useCustomer();
  const [editingAddressId, setEditingAddressId] = useState(addressToEdit);

  // ─── Active Bag Address Edit ──────────────────────────────────────────
  const { cart, fetchCart, isCartEmpty } = useCart();
  const currentBagAddressId = cart?.deliveryOrderDetail?.address?.id;
  const editingBagAddress = currentBagAddressId === editingAddressId;
  const shouldUpdateCart =
    checkExistingBag && !isCartEmpty && editingBagAddress;

  const [addAddressResponse, addAddress] = useAddAddressMutation();
  const [updateAddressResponse, updateAddress] = useUpdateAddressMutation();
  const [deleteAddressResponse, deleteAddress] = useDeleteAddressMutation();

  // ─── Telemetry ────────────────────────────────────────────────────────
  const { monitoredOnFail } = useAddressesTelemetry({});

  // ─── Locale ───────────────────────────────────────────────────────────
  const { t } = useLocalizationContext();
  const genericSaveFailure = t('account.address.failure.generic');
  const genericDeleteFailure = t('account.address.delete-failure.generic');
  const zipCodeFailure = t('account.address.failure.zip-code');
  const streetNumberFailure = t('account.address.failure.street-number');

  // ─── Notice Banners ───────────────────────────────────────────────────────────
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const notifyError = useCallback(
    (errorMessage: string) => {
      addNoticeBanner({
        text: errorMessage,
        palette: 'caution',
      });
    },
    [addNoticeBanner],
  );

  // ─── Address Validation ───────────────────────────────────────────────
  const validateAddress = useCallback(
    (form: Form) => {
      if (!form?.zipCode) {
        monitoredOnFail(streetNumberFailure);
        notifyError(zipCodeFailure);

        return false;
      }

      if (form?.street?.includes('undefined')) {
        monitoredOnFail(streetNumberFailure);
        notifyError(streetNumberFailure);

        return false;
      }

      return true;
    },
    [monitoredOnFail, notifyError, streetNumberFailure, zipCodeFailure],
  );

  // ─── CALLBACKS ────────────────────────────────────────────────────────
  const handleSetEditing = useCallback(
    (id: string) => {
      setEditingAddressId(id);
    },
    [setEditingAddressId],
  );

  const handleOnClose = useCallback(() => {
    setEditingAddressId('');

    if (onClose) onClose();
  }, [setEditingAddressId, onClose]);

  const handleOnSave = useCallback(
    (form: Form) => {
      (async () => {
        if (!validateAddress(form)) return;

        const response = await addAddress({
          input: form,
        });
        const data = response?.data?.addAddress;

        if (data?.__typename === 'AddAddressSuccess') {
          const savedAddress = data?.address;

          if (savedAddress) {
            setCustomerData({
              ...customer,
              addresses: [...(customer?.addresses ?? []), savedAddress],
            });
          }

          if (shouldUpdateCart) fetchCart();
          handleOnClose();
        } else {
          monitoredOnFail(
            genericSaveFailure,
            getUrqlError(response.error, response.data as never),
          );
          notifyError(genericSaveFailure);
        }
      })();
    },
    [
      customer,
      genericSaveFailure,
      shouldUpdateCart,
      validateAddress,
      fetchCart,
      handleOnClose,
      setCustomerData,
      addAddress,
      monitoredOnFail,
      notifyError,
    ],
  );

  const handleOnEdit = useCallback(
    (form: Form) => {
      (async () => {
        if (!editingAddressId) return;

        if (!validateAddress(form)) return;

        const response = await updateAddress({
          input: { ...form, id: editingAddressId },
        });
        const data = response.data?.updateAddress;

        if (data?.__typename === 'UpdateAddressSuccess') {
          const addresses = customer?.addresses ?? [];
          const updatedId = data?.address.id;
          const filtered = addresses.filter(({ id }) => id !== updatedId);
          const updatedAddresses = [...filtered, data.address];

          setCustomerData({
            ...customer,
            addresses: updatedAddresses,
          });

          if (shouldUpdateCart) fetchCart();
          handleOnClose();
        } else {
          monitoredOnFail(
            genericSaveFailure,
            getUrqlError(response.error, response.data as never),
          );
          notifyError(genericSaveFailure);
        }
      })();
    },
    [
      customer,
      shouldUpdateCart,
      genericSaveFailure,
      editingAddressId,
      validateAddress,
      setCustomerData,
      fetchCart,
      handleOnClose,
      updateAddress,
      monitoredOnFail,
      notifyError,
    ],
  );

  const handleOnDelete = useCallback(() => {
    (async () => {
      if (!editingAddressId) return;

      const response = await deleteAddress({
        id: editingAddressId,
      });

      const data = response?.data?.deleteAddress;

      if (data?.__typename === 'DeleteAddressSuccess') {
        const remainingAddresses = (customer?.addresses ?? []).filter(
          (curr) => curr.id !== editingAddressId,
        );

        if (response) {
          setCustomerData({
            ...customer,
            addresses: remainingAddresses,
          });
        }

        if (shouldUpdateCart) fetchCart();
        handleOnClose();
      } else {
        monitoredOnFail(
          genericDeleteFailure,
          getUrqlError(response.error, response.data as never),
        );
        notifyError(genericDeleteFailure);
      }
    })();
  }, [
    customer,
    genericDeleteFailure,
    editingAddressId,
    shouldUpdateCart,
    fetchCart,
    handleOnClose,
    setCustomerData,
    deleteAddress,
    monitoredOnFail,
    notifyError,
  ]);

  return {
    customer,
    isFetchingCustomer,
    editingAddressId,
    addAddressResponse,
    updateAddressResponse,
    deleteAddressResponse,
    handleOnSave,
    handleOnDelete,
    handleOnEdit,
    handleSetEditing,
    handleOnClose,
  };
};

export const useDuplicateCustomerAddressValidation = (addressId?: string) => {
  // Editing addresses has the address id in the route.
  const route = useRoute<RouteProp<ModalStackParamList, 'AddressEdit'>>();
  const editingAddressId = route.params?.addressId ?? addressId;

  // Customer Addresses.
  const { customer } = useCustomer();
  const addresses = customer?.addresses;

  const checkIfDuplicateAddressByStreet = useCallback(
    (form?: AddressForm) => {
      return addresses?.some((address) => {
        const hasSameId = address.id === editingAddressId;
        const hasSamePlaceId = address.googlePlaceId === form?.googlePlaceId;
        const hasSameStreet = address.street === form?.street;
        const hasSame2ndStreet =
          address.secondaryStreet === form?.secondaryStreet;
        const hasSameZipCode = address.zipCode === form?.zipCode;

        // Same Id means it's editing the current address, not duplicating it.
        if (hasSameId) return false;

        // Same Place Id means it's the same place (and a duplicate).
        if (hasSamePlaceId) return true;

        // Fallback in case google assigns a different place id for the same address.
        if (hasSameStreet && hasSame2ndStreet && hasSameZipCode) return true;

        // Addresses are not related.
        return false;
      });
    },
    [addresses, editingAddressId],
  );

  const checkIfDuplicateAddressByName = useCallback(
    (addressName?: string) => {
      return addresses?.some((address) => {
        const hasSameId = address.id === editingAddressId;
        const sameName =
          address.name?.toLowerCase() === addressName?.toLowerCase();

        // Same Id means it's editing the current address, not duplicating it.
        if (hasSameId) return false;

        // Duplicate by name.
        return sameName;
      });
    },
    [addresses, editingAddressId],
  );

  return useMemo(
    () => ({
      checkIfDuplicateAddressByStreet,
      checkIfDuplicateAddressByName,
    }),
    [checkIfDuplicateAddressByStreet, checkIfDuplicateAddressByName],
  );
};

type AddressHandlersProps = {
  readonly addressToEdit?: string;
  readonly checkExistingBag?: boolean;
  readonly onClose?: (() => void) | null;
};
